class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    // We are not re-enabling onFocusOut for the main menu at this time.
    // this.addEventListener('focusout', this.onFocusOut.bind(this)); 
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => {
      // Bind to ALL summaries, including the main hamburger menu
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });

    // Bind main close button
    const mainCloseButton = this.querySelector('.menu-drawer__header-container > .menu-drawer__close-wrapper');
    if (mainCloseButton) {
      mainCloseButton.addEventListener('click', this.onMainCloseButtonClick.bind(this));
    }

    // Bind submenu back buttons (previously submenu close buttons)
    this.querySelectorAll('.menu-drawer__submenu .menu-drawer__inner-submenu .menu-drawer__back-button').forEach((button) => {
      // Ensure not to double-bind if a more specific selector was already caught,
      // though current structure makes this unlikely for submenu close buttons.
      // Also ensure it's not a special button if those classes were ever added here by mistake
      if (button !== mainCloseButton && 
          !button.classList.contains('localization-selector') && 
          !button.classList.contains('country-selector__close-button') && 
          !button.classList.contains('country-filter__reset-button')) {
        button.addEventListener('click', this.onCloseButtonClick.bind(this)); // onCloseButtonClick calls closeSubmenu
      }
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    // Re-enable main menu escape handling
    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    // Re-enable Hamburger menu handling
    if (detailsElement === this.mainDetailsToggle) {
      event.preventDefault(); // Prevent default for the main toggle
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      // Handle submenu items only
      // event.preventDefault(); // Ensure this remains commented or removed for submenus
      
      // Check if we are about to open the details element
      // The 'open' attribute is added by the browser before this click event sometimes, 
      // or after for others. We want to add classes when it's opening.
      // A common pattern is to check !isOpen, but <details> toggles 'open' itself.
      // So if it was closed, it will be open after the click's default action.
      // If it was open, it will be closed.

      // If the details element is not currently open, it means this click will open it.
      if (!isOpen) {

        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        


        if (!reducedMotion || !reducedMotion.matches) {
          summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
        } else {
          addTrapFocus();
        }
      } else {
        // This click is closing the submenu (browser default action)
        detailsElement.classList.remove('menu-opening');
        summaryElement.setAttribute('aria-expanded', false);
        parentMenuElement && parentMenuElement.classList.remove('submenu-open');
        
        // Show main image if it exists and we are closing a submenu (returning to root)
        // Only show if no other submenus are open (i.e., we are back at the root level)
        const mainImage = this.mainDetailsToggle.querySelector('.menu-drawer__main-image');
        if (mainImage && !this.querySelector('details[open] > .menu-drawer__submenu')) {
           // Check if any other sibling submenus are open, this logic might need to be more robust
           // For now, if we are closing THIS submenu, and it was the one making the parent a submenu-open, 
           // we can assume we MIGHT be going back to root. 
           // A better check is needed if multiple submenus can be open under different root items.
           // Given the 2-level structure (root -> submenu), this is simpler.
           if (detailsElement.closest('.menu-drawer__menu.has-submenu > li > details') === detailsElement) {
             mainImage.style.display = ''; // Or 'block', 'flex', etc., depending on its original display style
           }
        }
      }
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      // Ensure it's not already open or opening to avoid race conditions
      if(!this.mainDetailsToggle.hasAttribute('open')) {
        this.mainDetailsToggle.setAttribute('open', '');
      }
      this.mainDetailsToggle.classList.remove('menu-closing'); // Clear closing class if any
      this.mainDetailsToggle.classList.add('menu-opening');
    }, 0); // Use timeout 0 to ensure 'open' attribute is set before class for transition
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined && !this.mainDetailsToggle.hasAttribute('open')) return; // Avoid closing if already closed by other means

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.classList.add('menu-closing'); // Add closing class for animation

    // Only reset attributes for sub-details, main one is handled by transitionend
    this.mainDetailsToggle.querySelectorAll('details:not(#Details-menu-drawer-container)').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });

    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);

    const drawer = this.mainDetailsToggle.querySelector('.menu-drawer'); // Or this.mainDetailsToggle directly if it is the drawer
    
    const onTransitionEnd = (e) => {
      if (e.target === drawer && e.propertyName === 'transform') {
        this.mainDetailsToggle.removeAttribute('open');
        this.mainDetailsToggle.classList.remove('menu-closing');
        if(elementToFocus && elementToFocus.isConnected) {
            elementToFocus.setAttribute('aria-expanded', false);
        }
        drawer.removeEventListener('transitionend', onTransitionEnd);
      }
    };

    if (drawer) {
      drawer.addEventListener('transitionend', onTransitionEnd);
    } else {
      // Fallback if no specific drawer element for transition (e.g., details directly controls visibility)
      this.mainDetailsToggle.removeAttribute('open');
      this.mainDetailsToggle.classList.remove('menu-closing');
      if(elementToFocus && elementToFocus.isConnected) {
        elementToFocus.setAttribute('aria-expanded', false);
      }
    }
    // Safety timeout if transitionend doesn't fire
    setTimeout(() => {
        if (this.mainDetailsToggle.classList.contains('menu-closing')) {
            this.mainDetailsToggle.removeAttribute('open');
            this.mainDetailsToggle.classList.remove('menu-closing');
            if(elementToFocus && elementToFocus.isConnected) {
                elementToFocus.setAttribute('aria-expanded', false);
            }
            if (drawer) drawer.removeEventListener('transitionend', onTransitionEnd);
        }
    }, 500); // Adjust timeout based on your transition duration

    if (elementToFocus && event instanceof KeyboardEvent) elementToFocus.setAttribute('aria-expanded', false);
  }
  
  onMainCloseButtonClick(event) {
    event.preventDefault();
    this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
  }

  onCloseButtonClick(event) { // This is for SUBMENU back/close buttons
    event.preventDefault();
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open'); // This would be the .has-submenu ul
    // If the detailsElement being closed is a direct child of .menu-drawer__menu (a root item's submenu)
    // then parentMenuElement will be the .menu-drawer__menu ul itself.
    if (parentMenuElement && parentMenuElement === detailsElement.closest('ul.has-submenu')){
        parentMenuElement.classList.remove('submenu-open');
    }
    
    detailsElement.classList.remove('menu-opening');
    detailsElement.classList.add('menu-closing'); // Add for animation
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    
    removeTrapFocus(detailsElement.querySelector('summary'));

    const submenu = detailsElement.querySelector('.menu-drawer__submenu'); // The div inside the <details>
    
    const onTransitionEnd = (e) => {
      // Ensure the event is for the correct element and property
      if (e.target === submenu && e.propertyName === 'transform') {
        detailsElement.removeAttribute('open');
        detailsElement.classList.remove('menu-closing');
        submenu.removeEventListener('transitionend', onTransitionEnd);

        // Show main image if it exists and we are back at the root level
        const mainImage = this.mainDetailsToggle.querySelector('.menu-drawer__main-image');
        if (mainImage && !this.mainDetailsToggle.querySelector('details[open] > .menu-drawer__submenu')) {
            // A more robust check for being at root: no other submenus are open inside mainDetailsToggle
            let isAtRoot = true;
            this.mainDetailsToggle.querySelectorAll('.menu-drawer__menu > li > details').forEach(detail => {
                if (detail.hasAttribute('open')) {
                    isAtRoot = false;
                }
            });
            if (isAtRoot) {
                mainImage.style.display = ''; // Or 'block', 'flex', etc.
            }
        }
      }
    };

    if (submenu) {
      submenu.addEventListener('transitionend', onTransitionEnd);
       // Safety timeout in case transitionend doesn't fire
      setTimeout(() => {
        if (detailsElement.classList.contains('menu-closing')) {
            detailsElement.removeAttribute('open');
            detailsElement.classList.remove('menu-closing');
            submenu.removeEventListener('transitionend', onTransitionEnd);
             // Also check for main image visibility here as a fallback
            const mainImage = this.mainDetailsToggle.querySelector('.menu-drawer__main-image');
            if (mainImage && !this.mainDetailsToggle.querySelector('details[open] > .menu-drawer__submenu')) {
                let isAtRoot = true;
                this.mainDetailsToggle.querySelectorAll('.menu-drawer__menu > li > details').forEach(detail => {
                    if (detail.hasAttribute('open')) {
                        isAtRoot = false;
                    }
                });
                if (isAtRoot) {
                    mainImage.style.display = '';
                }
            }
        }
      }, 500); // Adjust timeout based on your transition duration
    } else {
      // If no submenu element for transition (e.g., content is directly in details),
      // remove attributes directly, though this path is less likely with current structure.
      detailsElement.removeAttribute('open');
      detailsElement.classList.remove('menu-closing');
      // Show main image if applicable
      const mainImage = this.mainDetailsToggle.querySelector('.menu-drawer__main-image');
      if (mainImage && !this.mainDetailsToggle.querySelector('details[open] > .menu-drawer__submenu')) {
        let isAtRoot = true;
            this.mainDetailsToggle.querySelectorAll('.menu-drawer__menu > li > details').forEach(detail => {
                if (detail.hasAttribute('open')) {
                    isAtRoot = false;
                }
            });
        if (isAtRoot) {
            mainImage.style.display = '';
        }
      }
    }
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');
    super.openMenuDrawer(summaryElement); // Call parent method
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus && !this.mainDetailsToggle.hasAttribute('open')) return;
    super.closeMenuDrawer(event, elementToFocus); // Call parent method
    if (this.header) {
      this.header.classList.remove('menu-open');
    }
    window.removeEventListener('resize', this.onResize);
  }

  // onResize is fine here
  onResize = () => {
    if (this.mainDetailsToggle.hasAttribute('open')) { // Only run if menu is open
        this.header = this.header || document.querySelector('.section-header');
        if (this.header) {
            this.borderOffset =
            this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
            document.documentElement.style.setProperty(
            '--header-bottom-position',
            `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
            );
        }
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }
  };
}

customElements.define('header-drawer', HeaderDrawer); 


document.addEventListener('DOMContentLoaded', () => {
  const desktopMenus = document.querySelectorAll('.header__inline-menu details');
  
  // Create a function to properly reset content panels to base state
  function resetContentPanel(panel) {
    // Complete removal of all inline styles
    panel.removeAttribute('style');
    
    // Force browser reflow
    void panel.offsetWidth;
    
    // Set initial state for animation
    panel.style.opacity = '0';
    panel.style.transform = 'translateY(-1.5rem)';
    
    // Force another reflow to ensure initial state is applied
    void panel.offsetWidth;
  }
  
  desktopMenus.forEach((d) => {
    d.addEventListener('toggle', () => {
      const panel = d.querySelector('.mega-menu__content');
      if (!panel) return;

      /* desktop only */
      if (window.matchMedia('(min-width: 991px)').matches) {
        if (d.open) {
          /* opening – reset then let CSS animate it in */
          resetContentPanel(panel);
          
          // Remove inline styles to let CSS transitions take over
          panel.style.opacity = '';
          panel.style.transform = '';
        } else {
          /* closing – play fade/slide-out */
          panel.style.opacity = '0';
          panel.style.transform = 'translateY(-1.5rem)';
          panel.style.transition = 'opacity .25s ease, transform .25s ease';

          // Clear inline styles after transition completes
          setTimeout(() => {
            if (!d.open) {
              resetContentPanel(panel);
            }
          }, 300);
        }
      }
    });
    
    // Also reset on mouseenter to ensure proper animation on menu switches
    d.addEventListener('mouseenter', function() {
      if (d.open) return; // Skip if already open
      
      const panel = d.querySelector('.mega-menu__content');
      if (!panel) return;
      
      // Only for desktop
      if (window.matchMedia('(min-width: 991px)').matches) {
        resetContentPanel(panel);
      }
    });
  });
}); 

// Mobile mega-menu highlight animation
// --------------------------------------------------
document.addEventListener('DOMContentLoaded', function () {
  // Handle the mobile menu highlights
  const menuDrawer = document.querySelector('menu-drawer, header-drawer');
  if (!menuDrawer) return;
  
  // Function to reset mobile highlight to base state
  function resetMobileHighlight(highlight) {
    // Complete removal of all inline styles
    highlight.removeAttribute('style');
    
    // Force browser reflow
    void highlight.offsetWidth;
    
    // Set initial state for animation
    highlight.style.transform = 'scale(0)';
    highlight.style.opacity = '0';
    
    // Force another reflow to ensure initial state is applied
    void highlight.offsetWidth;
  }

  // Function to apply opening animation
  function animateHighlightOpen(highlight) {
    // First ensure reset
    resetMobileHighlight(highlight);
    
    // Then trigger animation with a slight delay to ensure proper sequencing
    setTimeout(() => {
      // Increase the duration values here to slow down the animation
      // Original: transform 0.4s cubic-bezier(0.68,-0.55,0.27,1.55), opacity 0.3s ease
      highlight.style.transition = 'transform 0.6s cubic-bezier(0.68,-0.55,0.27,1.55), opacity 0.5s ease';
      highlight.style.transform = 'scale(1)';
      highlight.style.opacity = '1';
    }, 10);
  }

  // Function to apply closing animation
  function animateHighlightClose(highlight, detail) {
    highlight.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
    highlight.style.transform = 'scale(0)';
    highlight.style.opacity = '0';
    
    // Clean up styles after animation
    setTimeout(() => {
      resetMobileHighlight(highlight);
    }, 250);
  }
  
  // Directly override the onSummaryClick method to intercept menu changes
  // This is the most reliable approach as it directly hooks into the menu controller
  const originalOnSummaryClick = MenuDrawer.prototype.onSummaryClick;
  if (originalOnSummaryClick) {
    MenuDrawer.prototype.onSummaryClick = function(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const isOpen = detailsElement.hasAttribute('open');
      
      // Call the original method first
      originalOnSummaryClick.call(this, event);
      
      // Now handle our highlight animation
      // Only for mobile (under 990px like theme uses)
      if (window.matchMedia('(max-width: 990px)').matches) {
        const highlight = detailsElement.querySelector('.mobile.mega-menu__highlight');
        if (highlight) {
          // If it wasn't open before, it should be opening now
          if (!isOpen) {
            // Opening
            animateHighlightOpen(highlight);
          } else {
            // Closing
            animateHighlightClose(highlight, detailsElement);
          }
        }
      }
    };
  }
  
  // Also override the closeSubmenu method to catch menu closing
  const originalCloseSubmenu = MenuDrawer.prototype.closeSubmenu;
  if (originalCloseSubmenu) {
    MenuDrawer.prototype.closeSubmenu = function(detailsElement) {
      // Check for mobile highlight before the element is modified
      const highlight = detailsElement.querySelector('.mobile.mega-menu__highlight');
      const isMobile = window.matchMedia('(max-width: 990px)').matches;
      
      // Call original method
      originalCloseSubmenu.call(this, detailsElement);
      
      // Handle our animation after the original method
      if (isMobile && highlight) {
        animateHighlightClose(highlight, detailsElement);
      }
    };
  }
  
  // Find all details elements inside the mobile menu that might contain highlights
  const mobileDetails = menuDrawer.querySelectorAll('.menu-drawer__menu details');
  
  mobileDetails.forEach((detail) => {
    // Find any mobile highlight in this details element
    const highlight = detail.querySelector('.mobile.mega-menu__highlight');
    if (!highlight) return; // Skip if no highlight
    
    // Pre-reset all highlights on page load
    resetMobileHighlight(highlight);
    
    // Create stronger direct event handlers instead of relying solely on MutationObserver
    
    // 1. Handle summary clicks (menu opening)
    const summary = detail.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', function(e) {
        // Only process for mobile
        if (!window.matchMedia('(max-width: 990px)').matches) return;
        
        // Since click toggles the state, check current state to determine direction
        const willOpen = !detail.hasAttribute('open');
        
        if (willOpen) {
          // This click will open the menu
          animateHighlightOpen(highlight);
        } else {
          // This click will close the menu
          animateHighlightClose(highlight, detail);
        }
      });
    }
    
    // 2. Handle direct open/close class changes
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        // Only process for mobile
        if (!window.matchMedia('(max-width: 990px)').matches) return;
        
        if (mutation.attributeName === 'class') {
          if (detail.classList.contains('menu-opening')) {
            animateHighlightOpen(highlight);
          } else if (detail.classList.contains('menu-closing')) {
            animateHighlightClose(highlight, detail);
          }
        } else if (mutation.attributeName === 'open') {
          // Direct open attribute change (backup method)
          if (detail.hasAttribute('open')) {
            animateHighlightOpen(highlight);
          } else {
            animateHighlightClose(highlight, detail);
          }
        }
      });
    });
    
    // Observe class and open changes
    observer.observe(detail, { 
      attributes: true, 
      attributeFilter: ['class', 'open'] 
    });
  });
  
  // 3. Handle back/close buttons explicitly
  const backButtons = menuDrawer.querySelectorAll('.menu-drawer__back-button, .menu-drawer__close-button');
  backButtons.forEach((button) => {
    button.addEventListener('click', function(e) {
      // Only process for mobile
      if (!window.matchMedia('(max-width: 990px)').matches) return;
      
      const detail = button.closest('details');
      if (!detail) return;
      
      const highlight = detail.querySelector('.mobile.mega-menu__highlight');
      if (!highlight) return;
      
      animateHighlightClose(highlight, detail);
    });
  });
  
  // 4. Handle direct submenu showing/hiding
  // Track when submenu transitions are happening
  const allSubmenuContainers = menuDrawer.querySelectorAll('.menu-drawer__submenu');
  allSubmenuContainers.forEach(submenu => {
    submenu.addEventListener('transitionstart', function(e) {
      // Only process for mobile
      if (!window.matchMedia('(max-width: 990px)').matches) return;
      
      // Only care about transform transitions
      if (e.propertyName !== 'transform') return;
      
      const detail = submenu.closest('details');
      if (!detail) return;
      
      const highlight = detail.querySelector('.mobile.mega-menu__highlight');
      if (!highlight) return;
      
      // If this is opening (transform going to translateX(0))
      const isOpening = !detail.classList.contains('menu-closing');
      
      if (isOpening) {
        animateHighlightOpen(highlight);
      } else {
        animateHighlightClose(highlight, detail);
      }
    });
  });
}); 

// get the height of the custom banner(when present) to correctly position the menu
document.addEventListener('DOMContentLoaded', function() {
  const customBanner = document.querySelector('.custom-top-info-banner');
  let height = 0;
  if (customBanner) {
    height = customBanner.offsetHeight;
  }
  document.documentElement.style.setProperty('--custom-banner-height', height + 'px');
});
